import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { Stethoscope, CheckCircle } from 'lucide-react';
export default function Specialist() {
  return <main className="min-h-screen bg-white" data-id="element-1299">
      <Helmet data-id="element-1300">
        <title data-id="element-1301">Psychiatric Reports for Courts & Tribunals UK</title>
        <meta name="description" content="Expert psychiatric reports for immigration tribunals, criminal courts, family courts, employment tribunals, and parole board hearings across the UK." data-id="element-1302" />
        <meta name="keywords" content="Psychiatric Reports for Courts & Tribunals, Psychiatric Expert for Solicitors and Barristers, Immigration Tribunals, Criminal Courts, Family Courts, Employment Tribunals, Expert Witness Psychiatrist, Psychiatric expert witness for parole board hearings" data-id="element-1303" />
        <script type="application/ld+json" data-id="element-1304">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Psychiatric Reports for Courts & Tribunals",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Specialist Psychiatric Evaluation",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert Psychiatric Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1305">
        <div className="container mx-auto px-4" data-id="element-1306">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1307">
            Psychiatric Reports for Courts & Tribunals
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1308">
            Tailored Expertise for Diverse Legal Needs
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1309">
        <div className="container mx-auto px-4" data-id="element-1310">
          <div className="max-w-3xl mx-auto" data-id="element-1311">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1312">
              The role of expert psychiatric opinion extends across a vast array
              of legal contexts, each with unique requirements. Dr. Imtiaz Syed
              provides highly specialised psychiatric evaluations tailored to
              the nuances of diverse legal settings, offering clarity and
              precise insights where mental health intersects with the law. His
              expertise ensures that the complexities of psychological factors
              are meticulously presented and understood, supporting informed
              decisions in various legal arenas.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1313">
              Why Instruct Dr. Imtiaz Syed for Specialist Evaluations?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1314">
              Dr. Syed's broad expertise across multiple legal domains ensures:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1315">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1316">
                <div className="flex items-start" data-id="element-1317">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1318" />
                  <div data-id="element-1319">
                    <h3 className="font-bold text-primary" data-id="element-1320">
                      Context-Specific Understanding
                    </h3>
                    <p className="text-slate" data-id="element-1321">
                      Reports are tailored to the specific legal questions and
                      frameworks of each tribunal or court.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1322">
                <div className="flex items-start" data-id="element-1323">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1324" />
                  <div data-id="element-1325">
                    <h3 className="font-bold text-primary" data-id="element-1326">
                      Comprehensive Coverage
                    </h3>
                    <p className="text-slate" data-id="element-1327">
                      Addressing mental health complexities in immigration,
                      family, employment, civil, and review tribunals.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1328">
                <div className="flex items-start" data-id="element-1329">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1330" />
                  <div data-id="element-1331">
                    <h3 className="font-bold text-primary" data-id="element-1332">
                      Experienced Courtroom Presence
                    </h3>
                    <p className="text-slate" data-id="element-1333">
                      Providing robust and clear expert testimony in diverse
                      legal settings.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1334">
                <div className="flex items-start" data-id="element-1335">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1336" />
                  <div data-id="element-1337">
                    <h3 className="font-bold text-primary" data-id="element-1338">
                      Nuanced Psychiatric Insights
                    </h3>
                    <p className="text-slate" data-id="element-1339">
                      Illuminating the interplay between mental health
                      conditions and legal outcomes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1340">
              Key Specialist Psychiatric Evaluations Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1341">
              Dr. Syed offers expert opinions for, but not limited to:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1342">
              <li className="flex items-start" data-id="element-1343">
                <span className="text-secondary font-bold mr-2" data-id="element-1344">•</span>
                <span data-id="element-1345">
                  <strong data-id="element-1346">Immigration and Asylum Tribunals:</strong> Assessing
                  the psychological impact of removal or deportation, and
                  evaluating potential risks upon return to an individual's home
                  country.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1347">
                <span className="text-secondary font-bold mr-2" data-id="element-1348">•</span>
                <span data-id="element-1349">
                  <strong data-id="element-1350">Criminal Courts:</strong> Expert psychiatric insights
                  relevant to criminal proceedings, beyond fitness to plead.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1351">
                <span className="text-secondary font-bold mr-2" data-id="element-1352">•</span>
                <span data-id="element-1353">
                  <strong data-id="element-1354">Family Courts:</strong> Expert opinions on parenting
                  capacity, domestic abuse, child protection, and other family
                  law matters.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1355">
                <span className="text-secondary font-bold mr-2" data-id="element-1356">•</span>
                <span data-id="element-1357">
                  <strong data-id="element-1358">Employment Tribunals:</strong> Evaluating mental
                  health impacts in workplace disputes, discrimination, or
                  personal injury claims related to employment.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1359">
                <span className="text-secondary font-bold mr-2" data-id="element-1360">•</span>
                <span data-id="element-1361">
                  <strong data-id="element-1362">Civil Claims and Personal Injury:</strong> Assessing
                  the psychiatric consequences of civil wrongs or injuries,
                  including prognosis and treatment recommendations.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1363">
                <span className="text-secondary font-bold mr-2" data-id="element-1364">•</span>
                <span data-id="element-1365">
                  <strong data-id="element-1366">Mental Health Review Tribunals:</strong> Providing
                  expert psychiatric assessments to inform decisions regarding
                  detention under the Mental Health Act.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1367">
                <span className="text-secondary font-bold mr-2" data-id="element-1368">•</span>
                <span data-id="element-1369">
                  <strong data-id="element-1370">Parole Board Hearings:</strong> Offering expert
                  evaluations to inform decisions regarding release from
                  custody, focusing on risk and mental state.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1371">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1372">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed offers both secure remote assessments across the UK and
              in-person consultations when appropriate, including visits to
              specific institutions as required. The resulting reports are
              always clear, legally relevant, and provide precise findings and
              actionable insights for your case, designed to withstand scrutiny.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1373">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1374">
              Dr. Imtiaz Syed is your trusted expert for navigating the
              intricate connections between mental health and the law across
              various legal landscapes. His clear, objective, and timely
              evaluations are invaluable in achieving just and informed
              decisions for your clients.
            </p>

            {/* Related Services */}
            <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1375">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1376">
                Explore Our Other Services
              </h3>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1377">
                <Link to="/services/trauma-ptsd" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1378">
                  PTSD & Trauma Psychiatric Evaluations
                </Link>
                <Link to="/services/mental-capacity" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1379">
                  Mental Capacity Assessments
                </Link>
                <Link to="/services/clinical-negligence" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1380">
                  Clinical Negligence Cases
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1381">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1382">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1383">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1384">
                <span className="mr-2 text-slate-dark" data-id="element-1385">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1386">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1387">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1388">
                Book a Specialist Psychiatric Evaluation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}